import React from 'react';

export type AppLanguage = 'en' | 'km' | 'ja';

export const LANGUAGE_STORAGE_KEY = 'camiyui_language';

export const languageOptions: Array<{ value: AppLanguage; label: string; shortLabel: string; htmlLang: string }> = [
  { value: 'en', label: 'English', shortLabel: 'EN', htmlLang: 'en' },
  { value: 'km', label: 'ខ្មែរ', shortLabel: 'KM', htmlLang: 'km' },
  { value: 'ja', label: '日本語', shortLabel: 'JA', htmlLang: 'ja' },
];

const messages: Record<AppLanguage, Record<string, string>> = {
  en: {
    'language.label': 'Language',
    'account.member': 'Member',
    'account.guest': 'Guest',
    'common.openExternal': 'Open {label}',
    'nav.home': 'Home',
    'nav.booking': 'Booking',
    'nav.menu': 'Menu',
    'nav.staff': 'Staff',
    'nav.myPage': 'My Page',
    'nav.messages': 'Messages',
    'nav.settings': 'Settings',
    'reserve.upcomingBooking': 'Upcoming Booking',
    'reserve.anyone': 'Anyone',
    'reserve.officialLinks': 'Official Links',
    'reserve.reservation': 'Reservation',
    'reserve.floatingButtonComingSoon': 'The reservation form will open here soon.',
    'booking.title': 'Booking',
    'booking.byStaff': 'Staff',
    'booking.byDate': 'Date',
    'booking.byService': 'Service',
    'booking.loadStoresFailed': 'Failed to load stores',
    'menu.title': 'Menu',
    'menu.loading': 'Loading categories...',
    'menu.empty': 'No menu categories are available yet.',
    'menu.other': 'Other',
    'menu.service': 'service',
    'menu.services': 'services',
    'staff.title': 'Staff',
    'staff.newStaff': 'New staff member',
    'staff.loading': 'Loading staff list...',
    'staff.empty': 'No staff are available yet.',
    'staff.fallbackName': 'Staff',
    'staff.bookWith': 'Book with {name}',
    'staff.thisStaff': 'this staff',
    'staff.bookingEyebrow': 'Staff booking',
    'staff.chooseTitle': 'Choose a staff',
    'staff.loadFailed': 'Failed to load staff members',
    'staff.updateFailed': 'Failed to update the selected staff',
    'staff.returnAfterServiceDateTime': 'Please return after selecting services, date, and time',
    'staff.loadingAvailable': 'Loading available staff...',
    'staff.loadingMembers': 'Loading staff members...',
    'staff.cannotChange': 'Staff cannot be changed',
    'staff.noAlternative': 'The current service, date, and time combination leaves no alternative staff choices. Please review the reservation conditions and create it again.',
    'staff.changeTo': 'Change to {name}',
    'staff.changeEyebrow': 'Staff change',
    'flow.back': 'Back',
    'flow.confirm': 'Confirm',
    'flow.bookingFlow': 'Booking flow',
    'flow.step': 'Step {step} / {totalSteps}',
    'date.chooseTitle': 'Choose a date',
    'date.selectStoreFirst': 'Please select a store first',
    'date.returnAfterSelections': 'Please return after selecting time, staff, and services',
    'date.selectServiceFirst': 'Please select at least one service',
    'date.selectStaffFirst': 'Please select a staff first',
    'date.returnAfterStaffTime': 'Please return after selecting staff option and time',
    'date.returnAfterTime': 'Please return after selecting a time',
    'date.rebookingUnavailable': 'Rebooking is not available right now',
    'date.noRebookingDates': 'There are no open dates for the same staff and services yet.',
    'date.noAlternativeService': 'The current services, staff, and time combination leaves no alternative dates.',
    'date.noAlternativeStaff': 'The current staff, services, and time combination leaves no alternative dates. Please review the reservation conditions and create it again.',
    'date.rebookingNotPrepared': 'Your last visit could not be prepared for rebooking.',
    'store.chooseTitle': 'Choose a store',
    'store.loadFailed': 'Failed to load stores',
    'store.openingFlow': 'Opening the booking flow...',
    'store.eyebrow': 'Store',
    'store.selectLocation': 'Select your location',
    'store.selectLocationHelp': 'Choose one store to continue the booking flow.',
    'store.loading': 'Loading stores...',
    'time.chooseTitle': 'Choose a time',
    'time.loadFailed': 'Failed to load available times',
    'time.returnAfterStaffServices': 'Please return after selecting staff and services',
    'time.selectWarning': 'Please select a time',
    'time.loading': 'Loading times...',
    'time.cannotChange': 'Time cannot be changed',
    'time.noAlternative': 'The current date, staff option, and services combination leaves no alternative times.',
    'time.noAvailable': 'There is no available time for this date.',
    'time.noAvailableShort': 'No available time.',
    'time.noRebookingTimes': 'There are no open times for the same staff and services on this date.',
    'time.noAlternativeServiceFlow': 'The current services, date, and staff combination leaves no alternative times.',
    'time.noAlternativeStaff': 'The current staff, date, and services combination leaves no alternative times.',
    'time.selectedSummaryWithMinutes': '{time} selected · {minutes} min',
    'time.selectSummaryWithMinutes': 'Select a time · {minutes} min',
    'time.reviewRebooking': 'Review rebooking',
    'time.invalidDuration': 'Service duration is invalid',
    'time.availableSlots': 'Available slots',
    'time.selectedSummary': '{time} selected',
    'time.selectSummary': 'Select a time',
    'service.chooseTitle': 'Choose services',
    'service.loadFailed': 'Failed to load services',
    'service.loading': 'Loading services...',
    'service.cannotChange': 'Service cannot be changed',
    'service.noAlternative': 'The current date, time, and staff option leave no alternative services.',
    'service.noAnyone': 'There are no services available for Anyone on this date.',
    'service.noStaff': 'There are no services available for the selected staff on this date.',
    'service.noDate': 'There are no services available on this date.',
    'service.noReservable': 'No reservable service is available.',
    'service.returnAfterDateStaffTime': 'Please return after selecting date, staff option, and time',
    'service.newAnyoneUnavailableDetail': 'The current date and time cannot accept this new service selection with Anyone is okay.',
    'service.newSelectionUnavailableDetail': 'The current date, staff, and time cannot accept this new service selection.',
    'service.newSelectionUnavailable': 'Service cannot be changed while keeping the current date, staff, and time. Please review the reservation conditions.',
    'service.incomplete': 'Reservation information is incomplete',
    'service.validateFailed': 'Failed to validate the selected services',
    'service.anyoneUnavailable': 'Anyone is not available for this time and selected services. Please change the services or choose a specific staff.',
    'service.anyoneUnavailableDetail': 'The current date and time cannot accept this service selection with Anyone is okay.',
    'service.staffUnavailable': 'The selected staff is not available for this time and service combination. Please review your staff or service selection.',
    'service.staffUnavailableDetail': 'The current date and time cannot accept this service selection for the selected staff.',
    'footer.next': 'Next',
    'footer.apply': 'Apply',
    'footer.checking': 'Checking...',
    'footer.selectedCount': '{count} selected · {total}',
    'footer.selectedCountWithMinutes': '{count} selected · {minutes} min · {total}',
    'footer.nextWithMinutes': 'Next ({minutes} min)',
    'footer.applyWithMinutes': 'Apply ({minutes} min)',
    'summary.store': 'Store',
    'summary.staff': 'Staff',
    'summary.date': 'Date',
    'summary.time': 'Time',
    'summary.services': 'Services',
    'summary.selected': 'Selected',
    'summary.pending': 'Pending',
    'summary.notSelected': 'Not selected',
    'summary.selectedCount': '{count} Selected',
    'summary.bookingFor': 'Booking for {name}',
    'confirm.title': 'Confirm your reservation',
    'confirm.rebookingTitle': 'Confirm your rebooking',
    'confirm.summary': 'Reservation summary',
    'confirm.selectedServices': 'Selected services',
    'confirm.change': 'Change',
    'confirm.total': 'Total',
    'confirm.amount': 'Amount',
    'confirm.confirming': 'Confirming...',
    'confirm.confirmReservation': 'Confirm reservation',
    'confirm.confirmRebooking': 'Confirm rebooking',
    'confirm.success': 'Your reservation has been confirmed',
    'confirm.rebookingSuccess': 'Your rebooking has been confirmed',
    'confirm.failed': 'Failed to confirm your reservation',
    'confirm.freeDurationNote': '{duration} depending on the staff in charge.',
    'confirm.startSummary': '{time} start · {duration} · {amount}',
    'confirm.timeSummary': '{start}-{end} · {minutes} min · {amount}',
    'date.loadFailed': 'Failed to load available dates',
    'date.unavailable': 'This date is not available for booking.',
    'date.loadingCalendar': 'Loading calendar...',
    'date.cannotChange': 'Date cannot be changed',
    'date.noAlternative': 'The current time, staff option, and services combination leaves no alternative dates.',
    'mypage.title': 'My Page',
    'mypage.accountCopy': 'Review your account and visit records here.',
    'mypage.personalInfo': 'Personal Information',
    'mypage.personalInfoDetail': 'Update your profile and phone numbers.',
    'mypage.points': 'Points',
    'mypage.pointsDetail': 'Check your available points and history.',
    'mypage.visitHistory': 'Visit History',
    'mypage.visitHistoryDetail': 'Check the details of your past visits.',
    'settings.title': 'Settings',
    'settings.activeAccount': 'Active account',
    'settings.notifications': 'Notifications',
    'settings.session': 'Session',
    'settings.signOut': 'Sign Out',
    'settings.signOutDetail': 'End this session on this device',
    'toast.signOutFallback': 'Failed to sign out cleanly, but this device will be reset.',
    'login.title': 'Login',
    'login.welcomeBack': 'Welcome back',
    'login.comingSoon': 'Coming Soon',
    'login.loginHelp': 'Log in with your phone number and password.',
    'login.maintenanceShort': 'Customer login is currently under maintenance.',
    'login.enterPhone': 'Please enter your phone number.',
    'login.enterPassword': 'Please enter your password.',
    'login.welcome': 'Welcome back, {name}!',
    'login.unavailableToast': 'The customer app is currently unavailable.',
    'login.maintenanceDetail': 'Customer app login is temporarily unavailable while we are preparing it. Please contact us from here.',
    'login.openTelegram': 'Open {store} Telegram',
    'login.contactTelegram': 'Contact via Telegram',
    'login.telegramChecking': 'Checking store Telegram links. Please wait a moment and try again.',
    'login.unavailableTitle': 'The customer app is currently unavailable',
    'login.unavailableDefault': 'You cannot log in until contract or service activation is confirmed.',
    'login.askStaff': 'Please contact store staff if you have any questions.',
    'intake.invalidTitle': 'Invalid intake link',
    'intake.invalidDetail': 'Ask your manager to start intake again and open the newest link from Telegram.',
    'intake.title': 'Customer Registration QR',
    'intake.reservationId': 'Reservation ID: {id}',
    'intake.instructions': 'Ask the customer to scan this QR code to register for this reservation.',
    'intake.qrAlt': 'Customer registration QR',
    'intake.urlLabel': 'Registration URL',
    'messages.title': 'Messages',
    'messages.inbox': 'Inbox',
    'messages.storeMessages': 'Store messages',
    'messages.unread': '{count} unread',
    'messages.latest': 'Latest',
    'messages.officialAccount': 'Official account',
    'messages.contactStore': 'Contact your store',
    'messages.openTelegram': 'Open {store} Telegram',
    'messages.inquiry': 'Inquiry & Contact',
    'messages.marking': 'Updating...',
    'messages.markAllRead': 'Mark all read',
    'messages.loading': 'Loading...',
    'messages.empty': 'No messages yet.',
    'messages.canceled': 'Canceled',
    'messages.confirmed': 'Confirmed',
    'messages.updated': 'Updated',
    'messages.followUp': 'Follow-up',
    'messages.reminder': 'Reminder',
    'messages.promotion': 'Promotion',
    'messages.new': 'New',
    'messages.fallbackTitle': 'Message from store',
    'messages.imageAlt': 'Store message image',
  },
  km: {
    'language.label': 'ភាសា',
    'account.member': 'សមាជិក',
    'account.guest': 'ភ្ញៀវ',
    'common.openExternal': 'បើក {label}',
    'nav.home': 'ទំព័រដើម',
    'nav.booking': 'ការកក់',
    'nav.menu': 'ម៉ឺនុយ',
    'nav.staff': 'បុគ្គលិក',
    'nav.myPage': 'ទំព័រខ្ញុំ',
    'nav.messages': 'សារ',
    'nav.settings': 'ការកំណត់',
    'reserve.upcomingBooking': 'ការកក់ខាងមុខ',
    'reserve.anyone': 'អ្នកណាក៏បាន',
    'reserve.officialLinks': 'តំណផ្លូវការ',
    'reserve.reservation': 'ការកក់',
    'reserve.floatingButtonComingSoon': 'ទម្រង់កក់នឹងបើកនៅទីនេះឆាប់ៗនេះ។',
    'booking.title': 'ការកក់',
    'booking.byStaff': 'បុគ្គលិក',
    'booking.byDate': 'កាលបរិច្ឆេទ',
    'booking.byService': 'សេវាកម្ម',
    'booking.loadStoresFailed': 'ផ្ទុកហាងបរាជ័យ',
    'menu.title': 'ម៉ឺនុយ',
    'menu.loading': 'កំពុងផ្ទុកប្រភេទម៉ឺនុយ...',
    'menu.empty': 'មិនទាន់មានប្រភេទម៉ឺនុយទេ។',
    'menu.other': 'ផ្សេងៗ',
    'menu.service': 'សេវាកម្ម',
    'menu.services': 'សេវាកម្ម',
    'staff.title': 'បុគ្គលិក',
    'staff.newStaff': 'បុគ្គលិកថ្មី',
    'staff.loading': 'កំពុងផ្ទុកបញ្ជីបុគ្គលិក...',
    'staff.empty': 'មិនទាន់មានបុគ្គលិកទេ។',
    'staff.fallbackName': 'បុគ្គលិក',
    'staff.bookWith': 'កក់ជាមួយ {name}',
    'staff.thisStaff': 'បុគ្គលិកនេះ',
    'staff.bookingEyebrow': 'ការកក់តាមបុគ្គលិក',
    'staff.chooseTitle': 'ជ្រើសរើសបុគ្គលិក',
    'staff.loadFailed': 'ផ្ទុកបុគ្គលិកបរាជ័យ',
    'staff.updateFailed': 'ធ្វើបច្ចុប្បន្នភាពបុគ្គលិកដែលបានជ្រើសបរាជ័យ',
    'staff.returnAfterServiceDateTime': 'សូមត្រឡប់មកវិញបន្ទាប់ពីជ្រើសសេវាកម្ម កាលបរិច្ឆេទ និងពេលវេលា',
    'staff.loadingAvailable': 'កំពុងផ្ទុកបុគ្គលិកដែលទំនេរ...',
    'staff.loadingMembers': 'កំពុងផ្ទុកបុគ្គលិក...',
    'staff.cannotChange': 'មិនអាចប្តូរបុគ្គលិកបាន',
    'staff.noAlternative': 'ការរួមបញ្ចូលសេវាកម្ម កាលបរិច្ឆេទ និងពេលវេលាបច្ចុប្បន្ន មិនមានជម្រើសបុគ្គលិកផ្សេងទេ។ សូមពិនិត្យលក្ខខណ្ឌការកក់ ហើយបង្កើតម្តងទៀត។',
    'staff.changeTo': 'ប្តូរទៅ {name}',
    'staff.changeEyebrow': 'ប្តូរបុគ្គលិក',
    'flow.back': 'ត្រឡប់',
    'flow.confirm': 'បញ្ជាក់',
    'flow.bookingFlow': 'ដំណើរការកក់',
    'flow.step': 'ជំហាន {step} / {totalSteps}',
    'date.chooseTitle': 'ជ្រើសរើសកាលបរិច្ឆេទ',
    'date.selectStoreFirst': 'សូមជ្រើសរើសហាងជាមុនសិន',
    'date.returnAfterSelections': 'សូមត្រឡប់មកវិញបន្ទាប់ពីជ្រើសពេលវេលា បុគ្គលិក និងសេវាកម្ម',
    'date.selectServiceFirst': 'សូមជ្រើសរើសសេវាកម្មយ៉ាងហោចណាស់មួយ',
    'date.selectStaffFirst': 'សូមជ្រើសរើសបុគ្គលិកជាមុនសិន',
    'date.returnAfterStaffTime': 'សូមត្រឡប់មកវិញបន្ទាប់ពីជ្រើសជម្រើសបុគ្គលិក និងពេលវេលា',
    'date.returnAfterTime': 'សូមត្រឡប់មកវិញបន្ទាប់ពីជ្រើសពេលវេលា',
    'date.rebookingUnavailable': 'មិនអាចកក់ឡើងវិញបាននៅពេលនេះ',
    'date.noRebookingDates': 'មិនមានកាលបរិច្ឆេទទំនេរសម្រាប់បុគ្គលិក និងសេវាកម្មដូចមុនទេ។',
    'date.noAlternativeService': 'ការរួមបញ្ចូលសេវាកម្ម បុគ្គលិក និងពេលវេលាបច្ចុប្បន្ន មិនមានកាលបរិច្ឆេទជំនួសទេ។',
    'date.noAlternativeStaff': 'ការរួមបញ្ចូលបុគ្គលិក សេវាកម្ម និងពេលវេលាបច្ចុប្បន្ន មិនមានកាលបរិច្ឆេទជំនួសទេ។ សូមពិនិត្យលក្ខខណ្ឌការកក់ ហើយបង្កើតម្តងទៀត។',
    'date.rebookingNotPrepared': 'មិនអាចរៀបចំការមកលើកមុនសម្រាប់កក់ឡើងវិញបានទេ។',
    'store.chooseTitle': 'ជ្រើសរើសហាង',
    'store.loadFailed': 'ផ្ទុកហាងបរាជ័យ',
    'store.openingFlow': 'កំពុងបើកដំណើរការកក់...',
    'store.eyebrow': 'ហាង',
    'store.selectLocation': 'ជ្រើសរើសទីតាំង',
    'store.selectLocationHelp': 'ជ្រើសរើសហាងមួយដើម្បីបន្តដំណើរការកក់។',
    'store.loading': 'កំពុងផ្ទុកហាង...',
    'time.chooseTitle': 'ជ្រើសរើសពេលវេលា',
    'time.loadFailed': 'ផ្ទុកពេលវេលាទំនេរបរាជ័យ',
    'time.returnAfterStaffServices': 'សូមត្រឡប់មកវិញបន្ទាប់ពីជ្រើសបុគ្គលិក និងសេវាកម្ម',
    'time.selectWarning': 'សូមជ្រើសរើសពេលវេលា',
    'time.loading': 'កំពុងផ្ទុកពេលវេលា...',
    'time.cannotChange': 'មិនអាចប្តូរពេលវេលាបាន',
    'time.noAlternative': 'ការរួមបញ្ចូលកាលបរិច្ឆេទ បុគ្គលិក និងសេវាកម្មបច្ចុប្បន្ន មិនមានពេលវេលាជំនួសទេ។',
    'time.noAvailable': 'មិនមានពេលទំនេរសម្រាប់កាលបរិច្ឆេទនេះទេ។',
    'time.noAvailableShort': 'មិនមានពេលទំនេរ។',
    'time.noRebookingTimes': 'មិនមានពេលទំនេរសម្រាប់បុគ្គលិក និងសេវាកម្មដូចមុននៅកាលបរិច្ឆេទនេះទេ។',
    'time.noAlternativeServiceFlow': 'ការរួមបញ្ចូលសេវាកម្ម កាលបរិច្ឆេទ និងបុគ្គលិកបច្ចុប្បន្ន មិនមានពេលវេលាជំនួសទេ។',
    'time.noAlternativeStaff': 'ការរួមបញ្ចូលបុគ្គលិក កាលបរិច្ឆេទ និងសេវាកម្មបច្ចុប្បន្ន មិនមានពេលវេលាជំនួសទេ។',
    'time.selectedSummaryWithMinutes': 'បានជ្រើស {time} · {minutes} នាទី',
    'time.selectSummaryWithMinutes': 'ជ្រើសរើសពេលវេលា · {minutes} នាទី',
    'time.reviewRebooking': 'ពិនិត្យការកក់ឡើងវិញ',
    'time.invalidDuration': 'រយៈពេលសេវាកម្មមិនត្រឹមត្រូវ',
    'time.availableSlots': 'ពេលទំនេរ',
    'time.selectedSummary': 'បានជ្រើស {time}',
    'time.selectSummary': 'ជ្រើសរើសពេលវេលា',
    'service.chooseTitle': 'ជ្រើសរើសសេវាកម្ម',
    'service.loadFailed': 'ផ្ទុកសេវាកម្មបរាជ័យ',
    'service.loading': 'កំពុងផ្ទុកសេវាកម្ម...',
    'service.cannotChange': 'មិនអាចប្តូរសេវាកម្មបាន',
    'service.noAlternative': 'កាលបរិច្ឆេទ ពេលវេលា និងជម្រើសបុគ្គលិកបច្ចុប្បន្ន មិនមានសេវាកម្មជំនួសទេ។',
    'service.noAnyone': 'មិនមានសេវាកម្មសម្រាប់ Anyone នៅកាលបរិច្ឆេទនេះទេ។',
    'service.noStaff': 'មិនមានសេវាកម្មសម្រាប់បុគ្គលិកដែលបានជ្រើសនៅកាលបរិច្ឆេទនេះទេ។',
    'service.noDate': 'មិនមានសេវាកម្មសម្រាប់កាលបរិច្ឆេទនេះទេ។',
    'service.noReservable': 'មិនមានសេវាកម្មអាចកក់បានទេ។',
    'service.returnAfterDateStaffTime': 'សូមត្រឡប់មកវិញបន្ទាប់ពីជ្រើសកាលបរិច្ឆេទ ជម្រើសបុគ្គលិក និងពេលវេលា',
    'service.newAnyoneUnavailableDetail': 'កាលបរិច្ឆេទ និងពេលវេលាបច្ចុប្បន្ន មិនអាចទទួលជម្រើសសេវាកម្មថ្មីនេះជាមួយ Anyone is okay បានទេ។',
    'service.newSelectionUnavailableDetail': 'កាលបរិច្ឆេទ បុគ្គលិក និងពេលវេលាបច្ចុប្បន្ន មិនអាចទទួលជម្រើសសេវាកម្មថ្មីនេះបានទេ។',
    'service.newSelectionUnavailable': 'មិនអាចប្តូរសេវាកម្មដោយរក្សាកាលបរិច្ឆេទ បុគ្គលិក និងពេលវេលាបច្ចុប្បន្នបានទេ។ សូមពិនិត្យលក្ខខណ្ឌការកក់។',
    'service.incomplete': 'ព័ត៌មានការកក់មិនទាន់គ្រប់គ្រាន់',
    'service.validateFailed': 'ផ្ទៀងផ្ទាត់សេវាកម្មដែលបានជ្រើសបរាជ័យ',
    'service.anyoneUnavailable': 'Anyone មិនអាចប្រើបានសម្រាប់ពេលវេលា និងសេវាកម្មដែលបានជ្រើស។ សូមប្តូរសេវាកម្ម ឬជ្រើសបុគ្គលិកជាក់លាក់។',
    'service.anyoneUnavailableDetail': 'កាលបរិច្ឆេទ និងពេលវេលាបច្ចុប្បន្ន មិនអាចទទួលសេវាកម្មនេះជាមួយ Anyone is okay បានទេ។',
    'service.staffUnavailable': 'បុគ្គលិកដែលបានជ្រើសមិនទំនេរសម្រាប់ពេលវេលា និងសេវាកម្មនេះទេ។ សូមពិនិត្យជម្រើសបុគ្គលិក ឬសេវាកម្ម។',
    'service.staffUnavailableDetail': 'កាលបរិច្ឆេទ និងពេលវេលាបច្ចុប្បន្ន មិនអាចទទួលសេវាកម្មនេះសម្រាប់បុគ្គលិកដែលបានជ្រើសបានទេ។',
    'footer.next': 'បន្ទាប់',
    'footer.apply': 'អនុវត្ត',
    'footer.checking': 'កំពុងពិនិត្យ...',
    'footer.selectedCount': 'បានជ្រើស {count} · {total}',
    'footer.selectedCountWithMinutes': 'បានជ្រើស {count} · {minutes} នាទី · {total}',
    'footer.nextWithMinutes': 'បន្ទាប់ ({minutes} នាទី)',
    'footer.applyWithMinutes': 'អនុវត្ត ({minutes} នាទី)',
    'summary.store': 'ហាង',
    'summary.staff': 'បុគ្គលិក',
    'summary.date': 'កាលបរិច្ឆេទ',
    'summary.time': 'ពេលវេលា',
    'summary.services': 'សេវាកម្ម',
    'summary.selected': 'បានជ្រើស',
    'summary.pending': 'មិនទាន់រួច',
    'summary.notSelected': 'មិនទាន់ជ្រើស',
    'summary.selectedCount': 'បានជ្រើស {count}',
    'summary.bookingFor': 'កក់សម្រាប់ {name}',
    'confirm.title': 'បញ្ជាក់ការកក់',
    'confirm.rebookingTitle': 'បញ្ជាក់ការកក់ឡើងវិញ',
    'confirm.summary': 'សង្ខេបការកក់',
    'confirm.selectedServices': 'សេវាកម្មដែលបានជ្រើស',
    'confirm.change': 'ប្តូរ',
    'confirm.total': 'សរុប',
    'confirm.amount': 'ចំនួនទឹកប្រាក់',
    'confirm.confirming': 'កំពុងបញ្ជាក់...',
    'confirm.confirmReservation': 'បញ្ជាក់ការកក់',
    'confirm.confirmRebooking': 'បញ្ជាក់ការកក់ឡើងវិញ',
    'confirm.success': 'ការកក់របស់អ្នកបានបញ្ជាក់ហើយ',
    'confirm.rebookingSuccess': 'ការកក់ឡើងវិញរបស់អ្នកបានបញ្ជាក់ហើយ',
    'confirm.failed': 'បញ្ជាក់ការកក់បរាជ័យ',
    'confirm.freeDurationNote': '{duration} អាស្រ័យលើបុគ្គលិកដែលទទួលបន្ទុក។',
    'confirm.startSummary': 'ចាប់ផ្តើម {time} · {duration} · {amount}',
    'confirm.timeSummary': '{start}-{end} · {minutes} នាទី · {amount}',
    'date.loadFailed': 'ផ្ទុកកាលបរិច្ឆេទដែលអាចកក់បានបរាជ័យ',
    'date.unavailable': 'កាលបរិច្ឆេទនេះមិនអាចកក់បានទេ។',
    'date.loadingCalendar': 'កំពុងផ្ទុកប្រតិទិន...',
    'date.cannotChange': 'មិនអាចប្តូរកាលបរិច្ឆេទបាន',
    'date.noAlternative': 'ការរួមបញ្ចូលពេលវេលា បុគ្គលិក និងសេវាកម្មបច្ចុប្បន្ន មិនមានកាលបរិច្ឆេទជំនួសទេ។',
    'mypage.title': 'ទំព័រខ្ញុំ',
    'mypage.accountCopy': 'ពិនិត្យគណនី និងប្រវត្តិមកហាងរបស់អ្នកនៅទីនេះ។',
    'mypage.personalInfo': 'ព័ត៌មានផ្ទាល់ខ្លួន',
    'mypage.personalInfoDetail': 'កែប្រែប្រវត្តិរូប និងលេខទូរស័ព្ទ។',
    'mypage.points': 'ពិន្ទុ',
    'mypage.pointsDetail': 'ពិនិត្យពិន្ទុដែលអាចប្រើបាន និងប្រវត្តិ។',
    'mypage.visitHistory': 'ប្រវត្តិមកហាង',
    'mypage.visitHistoryDetail': 'ពិនិត្យព័ត៌មានលម្អិតនៃការមកហាងមុនៗ។',
    'settings.title': 'ការកំណត់',
    'settings.activeAccount': 'គណនីសកម្ម',
    'settings.notifications': 'ការជូនដំណឹង',
    'settings.session': 'វគ្គចូល',
    'settings.signOut': 'ចេញ',
    'settings.signOutDetail': 'បញ្ចប់វគ្គនៅលើឧបករណ៍នេះ',
    'toast.signOutFallback': 'ចេញពីប្រព័ន្ធមិនបានពេញលេញ ប៉ុន្តែឧបករណ៍នេះនឹងត្រូវបានកំណត់ឡើងវិញ។',
    'login.title': 'ចូល',
    'login.welcomeBack': 'សូមស្វាគមន៍មកវិញ',
    'login.comingSoon': 'នឹងមកដល់ឆាប់ៗ',
    'login.loginHelp': 'ចូលដោយប្រើលេខទូរស័ព្ទ និងពាក្យសម្ងាត់។',
    'login.maintenanceShort': 'ការចូលអតិថិជនកំពុងថែទាំ។',
    'login.enterPhone': 'សូមបញ្ចូលលេខទូរស័ព្ទ។',
    'login.enterPassword': 'សូមបញ្ចូលពាក្យសម្ងាត់។',
    'login.welcome': 'សូមស្វាគមន៍មកវិញ {name}!',
    'login.unavailableToast': 'អាប់អតិថិជនមិនអាចប្រើបាននៅពេលនេះ។',
    'login.maintenanceDetail': 'ការចូលអាប់អតិថិជនមិនអាចប្រើបានបណ្តោះអាសន្ន ខណៈពេលកំពុងរៀបចំ។ សូមទាក់ទងមកយើងពីទីនេះ។',
    'login.openTelegram': 'បើក Telegram របស់ {store}',
    'login.contactTelegram': 'ទាក់ទងតាម Telegram',
    'login.telegramChecking': 'កំពុងពិនិត្យតំណ Telegram របស់ហាង។ សូមរង់ចាំបន្តិច ហើយសាកល្បងម្តងទៀត។',
    'login.unavailableTitle': 'អាប់អតិថិជនមិនអាចប្រើបាននៅពេលនេះ',
    'login.unavailableDefault': 'មិនអាចចូលបានទេ រហូតដល់បញ្ជាក់កិច្ចសន្យា ឬការបើកសេវាកម្ម។',
    'login.askStaff': 'បើមានចម្ងល់ សូមទាក់ទងបុគ្គលិកហាង។',
    'intake.invalidTitle': 'តំណចុះឈ្មោះមិនត្រឹមត្រូវ',
    'intake.invalidDetail': 'សូមស្នើអ្នកគ្រប់គ្រងឱ្យចាប់ផ្តើមការចុះឈ្មោះម្តងទៀត ហើយបើកតំណថ្មីបំផុតពី Telegram។',
    'intake.title': 'QR ចុះឈ្មោះអតិថិជន',
    'intake.reservationId': 'លេខសម្គាល់ការកក់: {id}',
    'intake.instructions': 'សូមឱ្យអតិថិជនស្កេន QR នេះ ដើម្បីចុះឈ្មោះសម្រាប់ការកក់នេះ។',
    'intake.qrAlt': 'QR ចុះឈ្មោះអតិថិជន',
    'intake.urlLabel': 'URL ចុះឈ្មោះ',
    'messages.title': 'សារ',
    'messages.inbox': 'ប្រអប់សារ',
    'messages.storeMessages': 'សារពីហាង',
    'messages.unread': 'មិនទាន់អាន {count}',
    'messages.latest': 'ថ្មីបំផុត',
    'messages.officialAccount': 'គណនីផ្លូវការ',
    'messages.contactStore': 'ទាក់ទងហាងរបស់អ្នក',
    'messages.openTelegram': 'បើក Telegram របស់ {store}',
    'messages.inquiry': 'សួរ និងទាក់ទង',
    'messages.marking': 'កំពុងធ្វើបច្ចុប្បន្នភាព...',
    'messages.markAllRead': 'សម្គាល់ថាអានទាំងអស់',
    'messages.loading': 'កំពុងផ្ទុក...',
    'messages.empty': 'មិនទាន់មានសារទេ។',
    'messages.canceled': 'បានលុបចោល',
    'messages.confirmed': 'បានបញ្ជាក់',
    'messages.updated': 'បានកែប្រែ',
    'messages.followUp': 'តាមដានក្រោយ',
    'messages.reminder': 'រំលឹក',
    'messages.promotion': 'ប្រូម៉ូសិន',
    'messages.new': 'ថ្មី',
    'messages.fallbackTitle': 'សារពីហាង',
    'messages.imageAlt': 'រូបភាពសារពីហាង',
  },
  ja: {
    'language.label': '言語',
    'account.member': 'メンバー',
    'account.guest': 'ゲスト',
    'common.openExternal': '{label} を開く',
    'nav.home': 'ホーム',
    'nav.booking': '予約',
    'nav.menu': 'メニュー',
    'nav.staff': 'スタッフ',
    'nav.myPage': 'マイページ',
    'nav.messages': 'メッセージ',
    'nav.settings': '設定',
    'reserve.upcomingBooking': '次回予約',
    'reserve.anyone': '指名なし',
    'reserve.officialLinks': '公式リンク',
    'reserve.reservation': '予約',
    'reserve.floatingButtonComingSoon': '予約フォームは近日中にここから開けるようになります。',
    'booking.title': '予約',
    'booking.byStaff': 'スタッフ',
    'booking.byDate': '日付',
    'booking.byService': 'サービス',
    'booking.loadStoresFailed': '店舗の読み込みに失敗しました',
    'menu.title': 'メニュー',
    'menu.loading': 'カテゴリーを読み込み中...',
    'menu.empty': '表示できるメニューカテゴリーはまだありません。',
    'menu.other': 'その他',
    'menu.service': 'サービス',
    'menu.services': 'サービス',
    'staff.title': 'スタッフ',
    'staff.newStaff': '新しいスタッフ',
    'staff.loading': 'スタッフ一覧を読み込み中...',
    'staff.empty': '表示できるスタッフはまだいません。',
    'staff.fallbackName': 'スタッフ',
    'staff.bookWith': '{name}で予約',
    'staff.thisStaff': 'このスタッフ',
    'staff.bookingEyebrow': 'スタッフ予約',
    'staff.chooseTitle': 'スタッフを選択',
    'staff.loadFailed': 'スタッフの読み込みに失敗しました',
    'staff.updateFailed': '選択スタッフの更新に失敗しました',
    'staff.returnAfterServiceDateTime': 'サービス、日付、時間を選択してから戻ってください',
    'staff.loadingAvailable': '対応可能スタッフを読み込み中...',
    'staff.loadingMembers': 'スタッフを読み込み中...',
    'staff.cannotChange': 'スタッフを変更できません',
    'staff.noAlternative': '現在のサービス、日付、時間の組み合わせでは、他に選択できるスタッフがいません。予約条件を見直して作成し直してください。',
    'staff.changeTo': '{name}に変更',
    'staff.changeEyebrow': 'スタッフ変更',
    'flow.back': '戻る',
    'flow.confirm': '確認',
    'flow.bookingFlow': '予約フロー',
    'flow.step': 'Step {step} / {totalSteps}',
    'date.chooseTitle': '日付を選択',
    'date.selectStoreFirst': '先に店舗を選択してください',
    'date.returnAfterSelections': '時間、スタッフ、サービスを選択してから戻ってください',
    'date.selectServiceFirst': 'サービスを1つ以上選択してください',
    'date.selectStaffFirst': '先にスタッフを選択してください',
    'date.returnAfterStaffTime': 'スタッフ候補と時間を選択してから戻ってください',
    'date.returnAfterTime': '時間を選択してから戻ってください',
    'date.rebookingUnavailable': '現在、再予約は利用できません',
    'date.noRebookingDates': '同じスタッフとサービスで予約できる日付がまだありません。',
    'date.noAlternativeService': '現在のサービス、スタッフ、時間の組み合わせでは、他に選択できる日付がありません。',
    'date.noAlternativeStaff': '現在のスタッフ、サービス、時間の組み合わせでは、他に選択できる日付がありません。予約条件を見直して作成し直してください。',
    'date.rebookingNotPrepared': '前回の来店情報を再予約用に準備できませんでした。',
    'store.chooseTitle': '店舗を選択',
    'store.loadFailed': '店舗の読み込みに失敗しました',
    'store.openingFlow': '予約フローを開いています...',
    'store.eyebrow': '店舗',
    'store.selectLocation': '店舗を選択',
    'store.selectLocationHelp': '予約フローを続ける店舗を1つ選択してください。',
    'store.loading': '店舗を読み込み中...',
    'time.chooseTitle': '時間を選択',
    'time.loadFailed': '予約可能時間の読み込みに失敗しました',
    'time.returnAfterStaffServices': 'スタッフとサービスを選択してから戻ってください',
    'time.selectWarning': '時間を選択してください',
    'time.loading': '時間を読み込み中...',
    'time.cannotChange': '時間を変更できません',
    'time.noAlternative': '現在の日付、スタッフ、サービスの組み合わせでは、他に選択できる時間がありません。',
    'time.noAvailable': 'この日付で予約できる時間はありません。',
    'time.noAvailableShort': '予約できる時間がありません。',
    'time.noRebookingTimes': 'この日付では、同じスタッフとサービスで予約できる時間がありません。',
    'time.noAlternativeServiceFlow': '現在のサービス、日付、スタッフの組み合わせでは、他に選択できる時間がありません。',
    'time.noAlternativeStaff': '現在のスタッフ、日付、サービスの組み合わせでは、他に選択できる時間がありません。',
    'time.selectedSummaryWithMinutes': '{time} 選択中 · {minutes}分',
    'time.selectSummaryWithMinutes': '時間を選択 · {minutes}分',
    'time.reviewRebooking': '再予約内容を確認',
    'time.invalidDuration': 'サービス時間が不正です',
    'time.availableSlots': '予約可能時間',
    'time.selectedSummary': '{time} 選択中',
    'time.selectSummary': '時間を選択',
    'service.chooseTitle': 'サービスを選択',
    'service.loadFailed': 'サービスの読み込みに失敗しました',
    'service.loading': 'サービスを読み込み中...',
    'service.cannotChange': 'サービスを変更できません',
    'service.noAlternative': '現在の日付、時間、スタッフ候補では、他に選択できるサービスがありません。',
    'service.noAnyone': 'この日付で指名なしに対応できるサービスはありません。',
    'service.noStaff': 'この日付で選択中スタッフに対応できるサービスはありません。',
    'service.noDate': 'この日付で利用できるサービスはありません。',
    'service.noReservable': '予約可能なサービスはありません。',
    'service.returnAfterDateStaffTime': '日付、スタッフ候補、時間を選択してから戻ってください',
    'service.newAnyoneUnavailableDetail': '現在の日付と時間では、この新しいサービス選択を指名なしで受け付けできません。',
    'service.newSelectionUnavailableDetail': '現在の日付、スタッフ、時間では、この新しいサービス選択を受け付けできません。',
    'service.newSelectionUnavailable': '現在の日付、スタッフ、時間を維持したままサービスを変更できません。予約条件を見直してください。',
    'service.incomplete': '予約情報が不足しています',
    'service.validateFailed': '選択したサービスの確認に失敗しました',
    'service.anyoneUnavailable': 'この時間とサービスでは指名なしを選択できません。サービスを変更するか、スタッフを指定してください。',
    'service.anyoneUnavailableDetail': '現在の日付と時間では、このサービス選択を指名なしで受け付けできません。',
    'service.staffUnavailable': '選択中のスタッフは、この時間とサービスの組み合わせに対応できません。スタッフまたはサービスを見直してください。',
    'service.staffUnavailableDetail': '現在の日付と時間では、選択中スタッフでこのサービス選択を受け付けできません。',
    'footer.next': '次へ',
    'footer.apply': '適用',
    'footer.checking': '確認中...',
    'footer.selectedCount': '{count}件選択 · {total}',
    'footer.selectedCountWithMinutes': '{count}件選択 · {minutes}分 · {total}',
    'footer.nextWithMinutes': '次へ ({minutes}分)',
    'footer.applyWithMinutes': '適用 ({minutes}分)',
    'summary.store': '店舗',
    'summary.staff': 'スタッフ',
    'summary.date': '日付',
    'summary.time': '時間',
    'summary.services': 'サービス',
    'summary.selected': '選択済み',
    'summary.pending': '未選択',
    'summary.notSelected': '未選択',
    'summary.selectedCount': '{count}件選択',
    'summary.bookingFor': '{name} の予約',
    'confirm.title': '予約内容を確認',
    'confirm.rebookingTitle': '再予約内容を確認',
    'confirm.summary': '予約サマリー',
    'confirm.selectedServices': '選択中のサービス',
    'confirm.change': '変更',
    'confirm.total': '合計',
    'confirm.amount': '金額',
    'confirm.confirming': '確認中...',
    'confirm.confirmReservation': '予約を確定',
    'confirm.confirmRebooking': '再予約を確定',
    'confirm.success': '予約が確定しました',
    'confirm.rebookingSuccess': '再予約が確定しました',
    'confirm.failed': '予約の確定に失敗しました',
    'confirm.freeDurationNote': '担当スタッフにより {duration} です。',
    'confirm.startSummary': '{time}開始 · {duration} · {amount}',
    'confirm.timeSummary': '{start}-{end} · {minutes}分 · {amount}',
    'date.loadFailed': '予約可能日の読み込みに失敗しました',
    'date.unavailable': 'この日付は予約できません。',
    'date.loadingCalendar': 'カレンダーを読み込み中...',
    'date.cannotChange': '日付を変更できません',
    'date.noAlternative': '現在の時間、スタッフ、サービスの組み合わせでは、他に選択できる日付がありません。',
    'mypage.title': 'マイページ',
    'mypage.accountCopy': 'アカウントと来店履歴をここで確認できます。',
    'mypage.personalInfo': '個人情報',
    'mypage.personalInfoDetail': 'プロフィールと電話番号を更新します。',
    'mypage.points': 'ポイント',
    'mypage.pointsDetail': '利用可能ポイントと履歴を確認します。',
    'mypage.visitHistory': '来店履歴',
    'mypage.visitHistoryDetail': '過去の来店詳細を確認します。',
    'settings.title': '設定',
    'settings.activeAccount': '有効なアカウント',
    'settings.notifications': '通知',
    'settings.session': 'セッション',
    'settings.signOut': 'サインアウト',
    'settings.signOutDetail': 'このデバイスのセッションを終了します',
    'toast.signOutFallback': '正常にサインアウトできませんでしたが、このデバイスの状態はリセットします。',
    'login.title': 'ログイン',
    'login.welcomeBack': 'おかえりなさい',
    'login.comingSoon': '準備中',
    'login.loginHelp': '電話番号とパスワードでログインしてください。',
    'login.maintenanceShort': '顧客ログインは現在メンテナンス中です。',
    'login.enterPhone': '電話番号を入力してください。',
    'login.enterPassword': 'パスワードを入力してください。',
    'login.welcome': 'おかえりなさい、{name}さん！',
    'login.unavailableToast': '現在、顧客アプリはご利用いただけません。',
    'login.maintenanceDetail': 'ただいま工事中につき、顧客アプリのログイン機能はしばらくご利用いただけません。ご連絡はこちらからお願いいたします。',
    'login.openTelegram': '{store} の Telegram を開く',
    'login.contactTelegram': 'Telegramで問い合わせ',
    'login.telegramChecking': '店舗のTelegramリンクを確認中です。少し時間をおいて再度お試しください。',
    'login.unavailableTitle': '顧客アプリは現在ご利用いただけません',
    'login.unavailableDefault': '契約またはサービス開通の確認が完了するまで、ログインできません。',
    'login.askStaff': 'ご不明な点は店舗スタッフまでお問い合わせください。',
    'intake.invalidTitle': '受付リンクが正しくありません',
    'intake.invalidDetail': '管理者に受付をもう一度開始してもらい、Telegramの最新リンクを開いてください。',
    'intake.title': '顧客登録QR',
    'intake.reservationId': '予約ID: {id}',
    'intake.instructions': 'この予約の登録のため、お客様にQRコードを読み取ってもらってください。',
    'intake.qrAlt': '顧客登録QR',
    'intake.urlLabel': '登録URL',
    'messages.title': 'メッセージ',
    'messages.inbox': '受信箱',
    'messages.storeMessages': '店舗メッセージ',
    'messages.unread': '未読 {count}件',
    'messages.latest': '最新',
    'messages.officialAccount': '公式アカウント',
    'messages.contactStore': '店舗へ問い合わせ',
    'messages.openTelegram': '{store} の Telegram を開く',
    'messages.inquiry': '問い合わせ・連絡',
    'messages.marking': '更新中...',
    'messages.markAllRead': 'すべて既読にする',
    'messages.loading': '読み込み中...',
    'messages.empty': 'まだメッセージはありません。',
    'messages.canceled': 'キャンセル',
    'messages.confirmed': '確定',
    'messages.updated': '更新',
    'messages.followUp': 'フォローアップ',
    'messages.reminder': 'リマインダー',
    'messages.promotion': 'プロモーション',
    'messages.new': '新着',
    'messages.fallbackTitle': '店舗からのメッセージ',
    'messages.imageAlt': '店舗メッセージ画像',
  },
};

type I18nContextValue = {
  language: AppLanguage;
  setLanguage: (language: AppLanguage) => void;
  t: (key: string, values?: Record<string, string | number>) => string;
};

const I18nContext = React.createContext<I18nContextValue | null>(null);

const normalizeLanguage = (value: string | null): AppLanguage => (
  value === 'km' || value === 'ja' || value === 'en' ? value : 'en'
);

const formatMessage = (message: string, values?: Record<string, string | number>) => {
  if (!values) return message;
  return Object.entries(values).reduce(
    (next, [key, value]) => next.split(`{${key}}`).join(String(value)),
    message,
  );
};

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = React.useState<AppLanguage>(() => (
    normalizeLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY))
  ));

  React.useEffect(() => {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    document.documentElement.lang = languageOptions.find((option) => option.value === language)?.htmlLang ?? 'en';
  }, [language]);

  React.useEffect(() => {
    const syncLanguage = (event: StorageEvent) => {
      if (event.key === LANGUAGE_STORAGE_KEY) {
        setLanguageState(normalizeLanguage(event.newValue));
      }
    };

    window.addEventListener('storage', syncLanguage);
    return () => window.removeEventListener('storage', syncLanguage);
  }, []);

  const value = React.useMemo<I18nContextValue>(() => ({
    language,
    setLanguage: setLanguageState,
    t: (key, values) => formatMessage(messages[language][key] ?? messages.en[key] ?? key, values),
  }), [language]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => {
  const context = React.useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return context;
};
